import React from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListItemNode, ListNode } from '@lexical/list';
import { LinkNode } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useEffect, useRef } from 'react';
import { $generateNodesFromDOM } from '@lexical/html';
import { $getRoot } from 'lexical';
import { sanitizeHTML } from '../utils/sanitize';
import { logger } from '../utils/logger';
import './LexicalViewer.css';

const theme = {
  heading: {
    h1: 'lexical-viewer-h1',
    h2: 'lexical-viewer-h2',
    h3: 'lexical-viewer-h3',
  },
  text: {
    bold: 'lexical-viewer-bold',
    italic: 'lexical-viewer-italic',
    underline: 'lexical-viewer-underline',
    strikethrough: 'lexical-viewer-strikethrough',
    code: 'lexical-viewer-code',
  },
  list: {
    nested: {
      listitem: 'lexical-viewer-nested-listitem',
    },
    ol: 'lexical-viewer-list-ol',
    ul: 'lexical-viewer-list-ul',
    listitem: 'lexical-viewer-listitem',
  },
  quote: 'lexical-viewer-quote',
  link: 'lexical-viewer-link',
};

// Load content for viewing
function ContentPlugin({ content }) {
  const [editor] = useLexicalComposerContext();
  const hasLoaded = useRef(false);
  
  useEffect(() => {
    if (hasLoaded.current) return;
    
    editor.setEditable(false);
    editor.update(() => {
      const root = $getRoot();
      root.clear();
      
      if (!content) {
        hasLoaded.current = true;
        return;
      }
      
      try {
        // Try to parse as JSON (Lexical editor state)
        const parsed = typeof content === 'string' ? JSON.parse(content) : content;
        
        if (parsed && parsed.root) {
          // It's a Lexical editor state JSON - load it directly
          const editorState = editor.parseEditorState(JSON.stringify(parsed));
          editor.setEditorState(editorState);
          hasLoaded.current = true;
          return;
        }
      } catch (e) {
        // Not JSON, continue to try HTML/text
      }
      
      // Try as HTML string - sanitize first to prevent XSS
      try {
        const sanitizedContent = sanitizeHTML(content);
        const parser = new DOMParser();
        const dom = parser.parseFromString(sanitizedContent, 'text/html');
        const nodes = $generateNodesFromDOM(editor, dom);
        root.append(...nodes);
        hasLoaded.current = true;
      } catch (e2) {
        // Fallback: treat as plain text (sanitized)
        const sanitizedText = sanitizeHTML(content);
        const textNode = root.createTextNode(sanitizedText);
        root.append(textNode);
        hasLoaded.current = true;
      }
    });
  }, [editor, content]);
  
  return null;
}

function LexicalViewer({ content, className }) {
  const initialConfig = {
    namespace: 'FeatureViewer',
    theme,
    onError: (error) => {
      logger.error('Lexical viewer error:', error);
    },
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      QuoteNode,
      LinkNode,
    ],
    editable: false,
  };

  return (
    <div className={`lexical-viewer-wrapper ${className || ''}`}>
      <LexicalComposer initialConfig={initialConfig}>
        <div className="lexical-viewer-container">
          <RichTextPlugin
            contentEditable={
              <ContentEditable className="lexical-viewer-content" />
            }
            placeholder={null}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <ContentPlugin content={content} />
        </div>
      </LexicalComposer>
    </div>
  );
}

export default LexicalViewer;
